import { useState, useEffect, useCallback, useRef } from 'react';
import { api, useAuthStore } from '../lib/auth';
import { turkishIncludes } from '../lib/turkishSearch';
import { formatLastSeenTr } from '../lib/relativeTime';
import { useSearchParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Search, Loader2, Send, User, Plus, Circle, MessageSquare,  ChevronLeft, ChevronRight, Check, CheckCheck, Wifi, WifiOff,
  Paperclip, Image as ImageIcon, FileText, Download, X, Eye, ZoomIn, ZoomOut, RotateCw, Volume2, VolumeX
} from 'lucide-react';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function Chat() {
  const { user, accessToken } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [msgLoading, setMsgLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Chat sound preference — read once, mutate via /me/preferences. Loaded from
  // the same backend the chat-heads use so toggling here also affects the
  // floating windows (and vice-versa) consistently.
  const [chatSound, setChatSound] = useState(true);
  useEffect(() => {
    let active = true;
    api.get('/me/preferences').then((res) => {
      if (active) setChatSound(!!res.data.data?.chat_sound_enabled);
    }).catch(() => {});
    return () => { active = false; };
  }, []);
  const toggleChatSound = async () => {
    const next = !chatSound;
    setChatSound(next);
    try { await api.put('/me/preferences', { chat_sound_enabled: next }); }
    catch { setChatSound(!next); toast.error('Ayar kaydedilemedi'); }
  };
  const [showContacts, setShowContacts] = useState(false); // WhatsApp-style: false=chats, true=new chat contacts
  const [onlineUsers, setOnlineUsers] = useState([]);
  // Per-user last_seen map. Populated from REST `/chat/online-users` and
  // `/chat/conversations` initially; updated locally to `now` when a user
  // transitions online -> offline via WS online_status events.
  const [lastSeenMap, setLastSeenMap] = useState({}); // { user_id: isoString }
  const [wsConnected, setWsConnected] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const [pendingFile, setPendingFile] = useState(null); // {file, preview}
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null); // {url, type, name, size}
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfPages, setPdfPages] = useState(null);
  const typingTimeoutRef = useRef(null);
  const typingThrottleRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch online users and conversations
  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/chat/online-users');
      if (res.data?.success) {
        const list = res.data.data || [];
        setUsers(list);
        setOnlineUsers(list.filter(u => u.is_online).map(u => u.id));
        setLastSeenMap(prev => {
          const next = { ...prev };
          list.forEach(u => {
            if (u.last_seen && !u.is_online) next[u.id] = u.last_seen;
          });
          return next;
        });
      }
    } catch {}
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await api.get('/chat/conversations');
      if (res.data?.success) {
        const list = res.data.data || [];
        setConversations(list);
        setLastSeenMap(prev => {
          const next = { ...prev };
          list.forEach(c => {
            if (c.last_seen && !c.is_online) next[c.partner_id] = c.last_seen;
          });
          return next;
        });
      }
    } catch {}
  }, []);

  const fetchChatHistory = useCallback(async (partnerId) => {
    setMsgLoading(true);
    try {
      const res = await api.get(`/chat/history/${partnerId}?per_page=100`);
      if (res.data?.success) setMessages(res.data.data || []);
    } catch {} finally { setMsgLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); fetchConversations(); }, [fetchUsers, fetchConversations]);

  // Minute-tick to auto-refresh "last seen" relative-time labels without re-fetching.
  const [, setMinuteTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setMinuteTick(n => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  // Auto-select user from URL parameter (?user=ID)
  useEffect(() => {
    const targetUserId = searchParams.get('user');
    if (!targetUserId || selectedUser) return;
    // Try to find in conversations first, then in users list
    const fromConvo = conversations.find(c => c.partner_id === targetUserId);
    if (fromConvo) {
      setSelectedUser(fromConvo);
      setSearchParams({}, { replace: true });
      return;
    }
    const fromUsers = users.find(u => u.id === targetUserId);
    if (fromUsers) {
      setSelectedUser({ id: fromUsers.id, name: fromUsers.full_name, email: fromUsers.email, role: fromUsers.role, is_online: fromUsers.is_online });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, conversations, users, selectedUser, setSearchParams]);

  // Refresh online status periodically - only as fallback when WS is disconnected.
  // WS is the authoritative source; REST polling here would otherwise race with
  // ws:online_status events and cause flicker on the status dot.
  useEffect(() => {
    const interval = setInterval(() => {
      const connected = window.__wsConnected || false;
      setWsConnected(connected);
      if (!connected) fetchUsers();
    }, 10000);
    setWsConnected(window.__wsConnected || false);
    return () => clearInterval(interval);
  }, [fetchUsers]);

  // Load chat history when selecting a user
  useEffect(() => {
    if (selectedUser) {
      fetchChatHistory(selectedUser.id || selectedUser.partner_id);
      inputRef.current?.focus();
    }
  }, [selectedUser, fetchChatHistory]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Listen for real-time chat events from DashboardLayout
  useEffect(() => {
    const handleChatMessage = (e) => {
      const msg = e.detail;
      const partnerId = selectedUser?.id || selectedUser?.partner_id;
      // If this message is part of the current conversation
      if (partnerId && (msg.sender_id === partnerId || msg.receiver_id === partnerId)) {
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
      }
      // Update conversations list
      fetchConversations();
    };

    const handleChatTyping = (e) => {
      const { user_id, user_name } = e.detail;
      const partnerId = selectedUser?.id || selectedUser?.partner_id;
      if (user_id === partnerId) {
        setTypingUser(user_name);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setTypingUser(null), 3000);
      }
    };

    const handleChatRead = (e) => {
      const { reader_id } = e.detail;
      const partnerId = selectedUser?.id || selectedUser?.partner_id;
      if (reader_id === partnerId) {
        setMessages(prev => prev.map(m =>
          m.sender_id === user?.id && !m.read ? { ...m, read: true } : m
        ));
      }
    };

    const handleOnlineStatus = (e) => {
      const onlineList = e.detail || [];
      // Detect users who just went offline and stamp their last_seen = now.
      setOnlineUsers(prev => {
        const wentOffline = prev.filter(id => !onlineList.includes(id));
        if (wentOffline.length > 0) {
          const nowIso = new Date().toISOString();
          setLastSeenMap(m => {
            const next = { ...m };
            wentOffline.forEach(id => { next[id] = nowIso; });
            return next;
          });
        }
        return onlineList;
      });
      setUsers(prev => prev.map(u => ({ ...u, is_online: onlineList.includes(u.id) })));
    };

    window.addEventListener('ws:chat_message', handleChatMessage);
    window.addEventListener('ws:chat_typing', handleChatTyping);
    window.addEventListener('ws:chat_read', handleChatRead);
    window.addEventListener('ws:online_status', handleOnlineStatus);
    return () => {
      window.removeEventListener('ws:chat_message', handleChatMessage);
      window.removeEventListener('ws:chat_typing', handleChatTyping);
      window.removeEventListener('ws:chat_read', handleChatRead);
      window.removeEventListener('ws:online_status', handleOnlineStatus);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [selectedUser, user?.id, fetchConversations]);

  // Send typing signal (throttled)
  const sendTypingSignal = () => {
    const partnerId = selectedUser?.id || selectedUser?.partner_id;
    if (!partnerId || typingThrottleRef.current) return;
    const sendFn = window.__sendWsMessage;
    if (sendFn) sendFn({ type: 'chat_typing', target_id: partnerId });
    typingThrottleRef.current = setTimeout(() => { typingThrottleRef.current = null; }, 2000);
  };

  const handleSend = async () => {
    const partnerId = selectedUser?.id || selectedUser?.partner_id;
    if ((!newMsg.trim() && !pendingFile) || !partnerId) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('receiver_id', partnerId);
      fd.append('message', newMsg.trim());
      if (pendingFile?.file) fd.append('file', pendingFile.file);
      const res = await api.post('/chat/send', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.success) {
        const msg = res.data.data;
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        setNewMsg('');
        clearPendingFile();
        fetchConversations();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mesaj gönderilemedi');
    } finally { setSending(false); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Dosya boyutu 20MB\'ı aşıyor');
      return;
    }
    const isImage = file.type.startsWith('image/');
    const preview = isImage ? URL.createObjectURL(file) : null;
    setPendingFile({ file, preview, name: file.name, size: file.size, isImage });
    e.target.value = '';
  };

  const clearPendingFile = () => {
    if (pendingFile?.preview) URL.revokeObjectURL(pendingFile.preview);
    setPendingFile(null);
  };

  // Preview attachment in-app (same as VehicleDocuments)
  const isImageType = (ct) => ct?.startsWith('image/');
  const isPdfType = (ct, name) => ct === 'application/pdf' || name?.toLowerCase().endsWith('.pdf');

  const handlePreview = async (msgId, attachment) => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewData(null);
    setPreviewError(null);
    setZoom(1);
    setRotation(0);
    setPdfPage(1);
    setPdfPages(null);
    try {
      const res = await api.get(`/chat/attachment/${msgId}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: attachment.content_type });
      const url = URL.createObjectURL(blob);
      setPreviewData({ url, type: attachment.content_type, name: attachment.file_name, size: attachment.file_size });
    } catch {
      setPreviewError('Dosya yüklenemedi');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewData?.url) URL.revokeObjectURL(previewData.url);
    setPreviewOpen(false);
    setPreviewData(null);
    setPreviewError(null);
  };

  // Download attachment via authenticated request
  const handleDownload = async (msgId, attachment) => {
    try {
      const res = await api.get(`/chat/attachment/${msgId}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: attachment.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('İndirme hatası');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Conversations list (only people I've chatted with, sorted by last message time)
  const chatList = conversations.map(c => ({
    id: c.partner_id,
    name: c.partner_name,
    email: c.partner_email,
    role: c.partner_role,
    is_online: onlineUsers.includes(c.partner_id),
    last_seen: lastSeenMap[c.partner_id] || c.last_seen,
    last_message: c.last_message,
    last_time: c.last_time,
    unread_count: c.unread_count,
  }));

  // All users list (for "new chat" mode) - exclude users already in conversations
  const convoIds = new Set(conversations.map(c => c.partner_id));
  const contactList = users.map(u => ({
    id: u.id,
    name: u.full_name,
    email: u.email,
    role: u.role,
    is_online: u.is_online || onlineUsers.includes(u.id),
    last_seen: lastSeenMap[u.id] || u.last_seen,
    last_message: null,
    last_time: null,
    unread_count: 0,
    inConvo: convoIds.has(u.id),
  }));

  // Filter by search (Turkish-aware)
  const filteredChats = chatList.filter(item => {
    if (!searchQuery) return true;
    return turkishIncludes(item.name, searchQuery) || turkishIncludes(item.email, searchQuery);
  });

  const filteredContacts = contactList.filter(item => {
    if (!searchQuery) return true;
    return turkishIncludes(item.name, searchQuery) || turkishIncludes(item.email, searchQuery);
  });

  const partnerId = selectedUser?.id || selectedUser?.partner_id;
  const partnerName = selectedUser?.name || selectedUser?.partner_name || selectedUser?.full_name || '';
  const partnerOnline = onlineUsers.includes(partnerId);

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 60000) return 'Az önce';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}dk`;
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
  };

  return (
    <>
    <div className="flex h-[calc(100vh-120px)] gap-0 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700" data-testid="chat-page">
      {/* Left sidebar - Chats / Contacts */}
      <div className={`${selectedUser ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 shrink-0 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700`}>
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center justify-between mb-3">
            {showContacts ? (
              <>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setShowContacts(false); setSearchQuery(''); }} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
                    <ChevronLeft className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                  </button>
                  <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Kişiler</h2>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100" data-testid="chat-title">Sohbet</h2>
                <div className="flex items-center gap-2">
                  {wsConnected ? (
                    <span className="flex items-center gap-1 text-[10px] text-emerald-500"><Wifi className="h-3 w-3" />Aktif</span>
                  ) : (
                    <span className="flex items-center gap-1 text-[10px] text-slate-400"><WifiOff className="h-3 w-3" />Bağlanıyor</span>
                  )}
                  <button
                    onClick={() => { setShowContacts(true); setSearchQuery(''); }}
                    className="p-1.5 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                    title="Yeni Sohbet"
                    data-testid="new-chat-btn"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              placeholder={showContacts ? "Kişi ara..." : "Sohbetlerde ara..."}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-8 h-9 text-sm bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
              data-testid="chat-search"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {showContacts ? (
            /* ===== CONTACTS LIST (New Chat) ===== */
            filteredContacts.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <User className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">Kişi bulunamadı</p>
              </div>
            ) : (
              filteredContacts.map(item => (
                <button
                  key={item.id}
                  onClick={() => { setSelectedUser(item); setShowContacts(false); setSearchQuery(''); }}
                  className="w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  data-testid={`chat-contact-${item.id}`}
                >
                  <div className="relative shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-600 dark:to-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-200 font-semibold text-sm">
                      {item.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-slate-900 ${item.is_online ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate block">{item.name}</span>
                    <span className="text-[10px] text-slate-400">
                      {item.role}
                      {item.is_online
                        ? ' - Çevrimiçi'
                        : item.last_seen
                          ? ` - ${formatLastSeenTr(item.last_seen)}`
                          : ''}
                    </span>
                  </div>
                  {item.inConvo && <Badge variant="outline" className="text-[9px] shrink-0">Mevcut</Badge>}
                </button>
              ))
            )
          ) : (
            /* ===== CONVERSATIONS LIST (Main) ===== */
            filteredChats.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">Henüz sohbet yok</p>
                <button
                  onClick={() => setShowContacts(true)}
                  className="mt-3 text-xs text-blue-500 hover:text-blue-600 font-medium"
                >
                  + Yeni sohbet başlat
                </button>
              </div>
            ) : (
              filteredChats.map(item => (
                <button
                  key={item.id}
                  onClick={() => setSelectedUser(item)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50 ${partnerId === item.id ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-500' : ''}`}
                  data-testid={`chat-contact-${item.id}`}
                >
                  <div className="relative shrink-0">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-600 dark:to-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-200 font-semibold text-sm">
                      {item.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white dark:border-slate-900 ${item.is_online ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm truncate ${item.unread_count > 0 ? 'font-bold text-slate-900 dark:text-slate-50' : 'font-medium text-slate-700 dark:text-slate-200'}`}>
                        {item.name}
                      </span>
                      {item.last_time && (
                        <span className="text-[10px] text-slate-400 shrink-0 ml-2">{formatTime(item.last_time)}</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <p className={`text-xs truncate ${item.unread_count > 0 ? 'text-slate-600 dark:text-slate-300 font-medium' : 'text-slate-400'}`}>
                        {item.last_message || ''}
                      </p>
                      {item.unread_count > 0 && (
                        <span className="min-w-[20px] h-5 flex items-center justify-center bg-blue-500 text-white text-[10px] font-bold rounded-full px-1.5 shrink-0 ml-2">
                          {item.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )
          )}
        </div>
      </div>

      {/* Right side - Chat window */}
      <div className={`${selectedUser ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-slate-50 dark:bg-slate-950`}>
        {selectedUser ? (
          <>
            {/* Chat header */}
            <div className="px-4 py-3 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
              <button className="md:hidden p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" onClick={() => setSelectedUser(null)}>
                <ChevronLeft className="h-5 w-5 text-slate-600 dark:text-slate-300" />
              </button>
              <div className="relative shrink-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-semibold text-sm">
                  {partnerName?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-slate-900 ${partnerOnline ? 'bg-emerald-500' : 'bg-slate-300'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate" data-testid="chat-partner-name">{partnerName}</h3>
                <p className="text-[11px] text-slate-400">
                  {typingUser ? (
                    <span className="text-blue-500 flex items-center gap-1">
                      yazıyor
                      <span className="inline-flex gap-0.5">
                        <span className="w-1 h-1 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1 h-1 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1 h-1 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                    </span>
                  ) : partnerOnline ? (
                    <span className="text-emerald-500">Çevrimiçi</span>
                  ) : (selectedUser?.last_seen || lastSeenMap[partnerId]) ? (
                    <span>son görülme {formatLastSeenTr(lastSeenMap[partnerId] || selectedUser?.last_seen)}</span>
                  ) : 'Çevrimdışı'}
                </p>
              </div>
              {/* Sohbet sesi toggle — header right side. Kept compact so it
                  doesn't compete with the partner name. Persists via
                  /me/preferences and mirrors the chat-head window button. */}
              <button
                onClick={toggleChatSound}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                title={chatSound ? 'Sohbet sesini kapat' : 'Sohbet sesini aç'}
                data-testid="chat-page-sound-toggle"
              >
                {chatSound
                  ? <Volume2 className="h-4 w-4 text-emerald-500" />
                  : <VolumeX className="h-4 w-4 text-slate-400" />}
              </button>
            </div>

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1" data-testid="chat-messages-area">
              {msgLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
              ) : messages.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                    <MessageSquare className="h-7 w-7 text-blue-400" />
                  </div>
                  <p className="text-sm text-slate-500">Sohbet başlatmak için bir mesaj gönderin</p>
                </div>
              ) : (
                <>
                  {messages.map((msg, idx) => {
                    const isMine = msg.sender_id === user?.id;
                    const showTime = idx === 0 || (new Date(msg.created_at) - new Date(messages[idx - 1]?.created_at)) > 300000;
                    return (
                      <div key={msg.id}>
                        {showTime && (
                          <div className="text-center my-3">
                            <span className="text-[10px] text-slate-400 bg-white dark:bg-slate-800 px-3 py-1 rounded-full shadow-sm">
                              {new Date(msg.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )}
                        <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-0.5`}>
                          <div
                            className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                              isMine
                                ? 'bg-blue-500 text-white rounded-br-md'
                                : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-bl-md shadow-sm border border-slate-100 dark:border-slate-700'
                            }`}
                            data-testid={`chat-msg-${msg.id}`}
                          >
                            {/* Attachment: Image or File */}
                            {msg.attachment && (
                              msg.attachment.is_image ? (
                                <button
                                  onClick={() => handlePreview(msg.id, msg.attachment)}
                                  className="block mb-1.5 text-left"
                                >
                                  <img
                                    src={`${API_URL}/api/chat/attachment/${msg.id}/inline?token=${accessToken}`}
                                    alt={msg.attachment.file_name}
                                    className="max-w-full max-h-60 rounded-xl object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                    loading="lazy"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handlePreview(msg.id, msg.attachment)}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-xl mb-1.5 transition-colors w-full text-left ${isMine ? 'bg-blue-600/30 hover:bg-blue-600/50' : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600'}`}
                                >
                                  <FileText className={`h-5 w-5 shrink-0 ${isMine ? 'text-blue-100' : 'text-blue-500'}`} />
                                  <div className="min-w-0 flex-1">
                                    <p className={`text-xs font-medium truncate ${isMine ? 'text-white' : 'text-slate-700 dark:text-slate-200'}`}>{msg.attachment.file_name}</p>
                                    <p className={`text-[10px] ${isMine ? 'text-blue-200' : 'text-slate-400'}`}>{(msg.attachment.file_size / 1024).toFixed(0)} KB</p>
                                  </div>
                                  <Eye className={`h-4 w-4 shrink-0 ${isMine ? 'text-blue-200' : 'text-blue-500'}`} />
                                </button>
                              )
                            )}
                            {msg.message && <p className="whitespace-pre-wrap break-words">{msg.message}</p>}
                            <div className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : ''}`}>
                              <span className={`text-[9px] ${isMine ? 'text-blue-100' : 'text-slate-400'}`}>
                                {new Date(msg.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              {isMine && (
                                msg.read ? (
                                  <CheckCheck className="h-3 w-3 text-blue-100" />
                                ) : (
                                  <Check className="h-3 w-3 text-blue-200/60" />
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input area */}
            <div className="px-4 py-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
              {/* Pending file preview */}
              {pendingFile && (
                <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                  {pendingFile.isImage && pendingFile.preview ? (
                    <img src={pendingFile.preview} alt="" className="h-14 w-14 rounded-lg object-cover" />
                  ) : (
                    <div className="h-14 w-14 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                      <FileText className="h-6 w-6 text-blue-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{pendingFile.name}</p>
                    <p className="text-[10px] text-slate-400">{(pendingFile.size / 1024).toFixed(0)} KB</p>
                  </div>
                  <button onClick={clearPendingFile} className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors" data-testid="chat-remove-file">
                    <X className="h-4 w-4 text-slate-400" />
                  </button>
                </div>
              )}
              <div className="flex items-end gap-2">
                {/* File attach button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="h-10 w-10 flex items-center justify-center rounded-full text-slate-400 hover:text-blue-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shrink-0"
                  title="Dosya/Resim Ekle"
                  data-testid="chat-attach-btn"
                >
                  <Paperclip className="h-5 w-5" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <div className="flex-1">
                  <Input
                    ref={inputRef}
                    placeholder="Mesajınızı yazın..."
                    value={newMsg}
                    onChange={e => { setNewMsg(e.target.value); sendTypingSignal(); }}
                    onKeyDown={handleKeyDown}
                    className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-full px-4"
                    data-testid="chat-input"
                  />
                </div>
                <Button
                  size="icon"
                  className="h-10 w-10 rounded-full shrink-0"
                  onClick={handleSend}
                  disabled={(!newMsg.trim() && !pendingFile) || sending}
                  data-testid="chat-send-btn"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </>
        ) : (
          /* Empty state */
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <div className="w-20 h-20 mb-6 rounded-full bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center">
              <MessageSquare className="h-9 w-9 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2">Sohbet Seçin</h3>
            <p className="text-sm text-slate-400 max-w-xs">
              Sol taraftaki listeden bir sohbet seçin veya yeni bir sohbet başlatın
            </p>
            <button
              onClick={() => setShowContacts(true)}
              className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
            >
              + Yeni Sohbet Başlat
            </button>
          </div>
        )}
      </div>
    </div>

    {/* File Preview Dialog (same as VehicleDocuments) */}
    <Dialog open={previewOpen} onOpenChange={closePreview}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold flex items-center gap-2">
              {isPdfType(previewData?.type, previewData?.name) ? (
                <FileText className="h-5 w-5 text-red-500" />
              ) : (
                <ImageIcon className="h-5 w-5 text-blue-500" />
              )}
              <span className="truncate max-w-md">{previewData?.name || 'Önizleme'}</span>
              {isPdfType(previewData?.type, previewData?.name) && pdfPages && (
                <Badge variant="outline" className="ml-2">{pdfPage} / {pdfPages}</Badge>
              )}
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4 py-2 border-b flex-shrink-0">
          {isPdfType(previewData?.type, previewData?.name) && (
            <>
              <Button variant="outline" size="sm" onClick={() => setPdfPage(p => Math.max(1, p - 1))} disabled={pdfPage <= 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">{pdfPage} / {pdfPages || '?'}</span>
              <Button variant="outline" size="sm" onClick={() => setPdfPage(p => Math.min(pdfPages || p, p + 1))} disabled={pdfPage >= (pdfPages || 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <div className="border-l pl-4 flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setZoom(s => Math.max(0.5, s - 0.25))}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
                <Button variant="outline" size="sm" onClick={() => setZoom(s => Math.min(2, s + 0.25))}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
          {isImageType(previewData?.type) && (
            <>
              <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.max(0.25, z - 0.25))}>
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
              <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.min(3, z + 0.25))}>
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setRotation(r => (r + 90) % 360)}>
                <RotateCw className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-900 rounded-lg flex items-start justify-center min-h-[400px] p-4">
          {previewLoading ? (
            <div className="flex flex-col items-center gap-3 py-20">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm text-slate-500">Yükleniyor...</p>
            </div>
          ) : previewError ? (
            <div className="text-center text-red-500 py-20">
              <p>{previewError}</p>
            </div>
          ) : isImageType(previewData?.type) && previewData?.url ? (
            <div className="overflow-auto w-full h-full flex items-center justify-center">
              <img
                src={previewData.url}
                alt={previewData.name}
                className="max-w-full transition-transform duration-200"
                style={{ transform: `scale(${zoom}) rotate(${rotation}deg)`, transformOrigin: 'center center' }}
              />
            </div>
          ) : isPdfType(previewData?.type, previewData?.name) && previewData?.url ? (
            <Document
              file={previewData.url}
              onLoadSuccess={({ numPages }) => setPdfPages(numPages)}
              onLoadError={() => setPreviewError('PDF yüklenemedi')}
              loading={<div className="flex items-center gap-2 py-20"><Loader2 className="h-6 w-6 animate-spin" /><span>PDF yükleniyor...</span></div>}
            >
              <Page pageNumber={pdfPage} scale={zoom} renderTextLayer={true} renderAnnotationLayer={true} />
            </Document>
          ) : (
            <div className="text-center p-8 py-20">
              <FileText className="h-12 w-12 mx-auto mb-4 text-slate-400" />
              <p className="text-slate-500 mb-4">Bu dosya türü önizlenemez</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="flex-shrink-0 border-t pt-4">
          <div className="flex justify-between items-center w-full">
            <span className="text-sm text-slate-500">
              {previewData?.size ? `${(previewData.size / 1024).toFixed(1)} KB` : ''}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={closePreview}>Kapat</Button>
              {previewData?.url && (
                <Button onClick={() => { const a = document.createElement('a'); a.href = previewData.url; a.download = previewData.name; a.click(); }}>
                  <Download className="h-4 w-4 mr-2" /> İndir
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
